import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FaArrowLeft,
  FaShieldAlt,
  FaCogs,
  FaAward,
  FaUsers,
  FaIndustry,
  FaMicrochip,
  FaCheckCircle,
  FaBoxes,
  FaTruck,
  FaHandshake,
  FaQuoteLeft,
  FaQuoteRight,
  FaBuilding,
  FaGlobe,
  FaChartLine,
  FaLeaf,
  FaBullseye,
  FaUserFriends,
  FaHistory
} from 'react-icons/fa';

/* ============================================================
   IMAGE IMPORTS
   ============================================================ */

import KKlogo from '../logos/kkindustries/kkindustrieslogo.png';
import KKhero from '../logos/kkindustries/kkindustries1.png';

import KKcarpet from '../logos/kkindustries/kkindustries2.png';
import KKprayer from '../logos/kkindustries/kkindustries3.png';
import KKmat from '../logos/kkindustries/kkindustries4.png';
import KKyarn from '../logos/kkindustries/kkindustries5.png';
import KKbacking from '../logos/kkindustries/kkindustries6.png';
import KKmachine from '../logos/kkindustries/kkindustries7.png';

import KKlegacy from '../logos/kkindustries/kkindustries8.png';

/* ============================================================
   COMPONENT
   ============================================================ */

export default function KKIndustries() {

  const navigate = useNavigate();

  return (
    <div className="kk-page">

      {/* ======================================================
          BACK BUTTON
      ====================================================== */}

      <div className="kk-top-nav">

        <button
          className="kk-back-button"
          onClick={() => navigate('/')}
        >
          <FaArrowLeft />
          <span>Back to All Sectors</span>
        </button>

      </div>


      {/* ======================================================
          HERO SECTION
      ====================================================== */}

      <section className="kk-hero">

        {/* LEFT SIDE */}

        <div className="kk-hero-left">

          <div className="kk-logo-container">

            <img
              src={KKlogo}
              alt="KK Industries Logo"
              className="kk-logo"
            />

          </div>


          <h2 className="kk-tagline">
            Strength. Quality. Legacy.
          </h2>


          <p className="kk-description">

            KK Industries is the
            <strong> backbone of Ashraf Group</strong>.
            With decades of experience, modern infrastructure
            and a relentless commitment to quality, we
            manufacture world-class products that power
            multiple industries and uplift communities.

          </p>


          {/* HERO PILLARS */}

          <div className="kk-hero-pillars">

            <div className="kk-hero-pillar">

              <FaShieldAlt />

              <h4>
                BUILT ON LEGACY
              </h4>

              <p>
                Decades of trust and excellence since inception.
              </p>

            </div>


            <div className="kk-hero-pillar">

              <FaCogs />

              <h4>
                DRIVEN BY INNOVATION
              </h4>

              <p>
                Advanced technology and continuous improvement.
              </p>

            </div>


            <div className="kk-hero-pillar">

              <FaAward />

              <h4>
                COMMITTED TO QUALITY
              </h4>

              <p>
                International standards in every process.
              </p>

            </div>


            <div className="kk-hero-pillar">

              <FaUsers />

              <h4>
                FOCUSED ON PEOPLE
              </h4>

              <p>
                Empowering our team and communities.
              </p>

            </div>

          </div>

        </div>


        {/* RIGHT SIDE */}

        <div className="kk-hero-right">

          <div className="kk-hero-image-container">

            <img
              src={KKhero}
              alt="KK Industries Manufacturing Facility"
              className="kk-hero-image"
            />


            <div className="kk-hero-overlay">

              <h2>
                MANUFACTURING EXCELLENCE.
                <br />
                DELIVERING VALUE.
              </h2>

              <div className="kk-hero-divider">
                ───────── ◇ ─────────
              </div>

              <p>
                We combine scale with precision to deliver
                products that meet global standards and
                customer expectations.
              </p>

            </div>

          </div>

        </div>

      </section>


      {/* ======================================================
          CORE CAPABILITIES HEADING
      ====================================================== */}

      <SectionHeading title="OUR CORE CAPABILITIES" />


      {/* ======================================================
          CORE CAPABILITIES
      ====================================================== */}

      <section className="kk-capabilities">

        <Capability
          icon={<FaIndustry />}
          title="LARGE SCALE MANUFACTURING"
          text="State-of-the-art facilities ensuring high production capacity and efficiency."
        />

        <Capability
          icon={<FaMicrochip />}
          title="ADVANCED TECHNOLOGY"
          text="Modern machinery and skilled workforce for superior performance."
        />

        <Capability
          icon={<FaCheckCircle />}
          title="QUALITY ASSURANCE"
          text="Rigorous inspection and quality control at every stage of production."
        />

        <Capability
          icon={<FaBoxes />}
          title="DIVERSE PRODUCT RANGE"
          text="Serving multiple industries with a wide range of reliable products."
        />

        <Capability
          icon={<FaTruck />}
          title="ON-TIME DELIVERY"
          text="Strong logistics and supply chain network to ensure timely delivery."
        />

        <Capability
          icon={<FaHandshake />}
          title="CUSTOMER FOCUSED"
          text="Long-term partnerships built on trust, transparency and value."
        />

      </section>


      {/* ======================================================
          PRODUCTS HEADING
      ====================================================== */}

      <SectionHeading title="OUR PRODUCTS" />


      {/* ======================================================
          PRODUCTS
      ====================================================== */}

      <section className="kk-products">

        <ProductCard
          image={KKcarpet}
          title="CARPETS"
          text={
            <>
              Quality you can feel.
              <br />
              Tradition you can trust.
            </>
          }
        />


        <ProductCard
          image={KKprayer}
          title="PRAYER MATS"
          text={
            <>
              Crafted with care.
              <br />
              Made for devotion.
            </>
          }
        />


        <ProductCard
          image={KKmat}
          title="MATS"
          text={
            <>
              Durable. Versatile.
              <br />
              Made for everyday.
            </>
          }
        />


        <ProductCard
          image={KKyarn}
          title="YARN"
          text={
            <>
              Strong fibers.
              <br />
              Stronger futures.
            </>
          }
        />


        <ProductCard
          image={KKbacking}
          title="BACKING SOLUTIONS"
          text={
            <>
              The foundation behind
              <br />
              every perfect carpet.
            </>
          }
        />


        <ProductCard
          image={KKmachine}
          title="& MORE"
          text={
            <>
              Expanding capabilities.
              <br />
              Endless possibilities.
            </>
          }
        />

      </section>


      {/* ======================================================
          FOUNDATION + VALUES
      ====================================================== */}

      <section className="kk-foundation-values">


        {/* FOUNDATION */}

        <div className="kk-foundation-card">

          <div className="kk-foundation-text">

            <h3>
              THE FOUNDATION OF THE GROUP
            </h3>

            <p>
              KK Industries is more than a business – it is
              the foundation that supports and strengthens
              the entire Ashraf Group. Our products, our
              people and our purpose drive progress across
              industries and generations.
            </p>

          </div>


          <div className="kk-legacy-image">

            <img
              src={KKlegacy}
              alt="KK Industries Legacy"
            />

            <span>
              LEGACY
            </span>

          </div>

        </div>


        {/* VALUES */}

        <div className="kk-values-card">

          <h3>
            OUR VALUES
          </h3>


          <div className="kk-values">

            <Value
              icon={<FaShieldAlt />}
              title="INTEGRITY"
              text="We do what is right, always."
            />

            <Value
              icon={<FaUserFriends />}
              title="TEAMWORK"
              text="Together we achieve more."
            />

            <Value
              icon={<FaBullseye />}
              title="EXCELLENCE"
              text="We aim higher every day."
            />

            <Value
              icon={<FaLeaf />}
              title="SUSTAINABILITY"
              text="Responsible today, better tomorrow."
            />

          </div>

        </div>

      </section>


      {/* ======================================================
          MOTTO
      ====================================================== */}

      <section className="kk-motto">


        <div className="kk-motto-text">

          <FaQuoteLeft />

          <div>

            <strong>
              BUILDING PRODUCTS THAT STRENGTHEN INDUSTRIES.
            </strong>

            <strong className="kk-motto-gold">
              BUILDING RELATIONSHIPS THAT LAST GENERATIONS.
            </strong>

          </div>

          <FaQuoteRight />

        </div>


        <div className="kk-stats">

          <Stat
            icon={<FaHistory />}
            text={
              <>
                YEARS OF
                <br />
                EXPERIENCE
              </>
            }
          />

          <Stat
            icon={<FaIndustry />}
            text={
              <>
                MODERN
                <br />
                INFRASTRUCTURE
              </>
            }
          />

          <Stat
            icon={<FaUsers />}
            text={
              <>
                SKILLED
                <br />
                WORKFORCE
              </>
            }
          />

          <Stat
            icon={<FaGlobe />}
            text={
              <>
                SERVING
                <br />
                GLOBAL MARKETS
              </>
            }
          />

        </div>

      </section>


      {/* ======================================================
          FOOTER
      ====================================================== */}

      <footer className="kk-footer">


        <div className="kk-footer-number">
          23
        </div>


        <div className="kk-footer-company">

          <strong>
            ASHRAF GROUP OF INDUSTRIES
          </strong>

          <span>
            Building Legacy. Powering the Future.
          </span>

        </div>


        <div className="kk-footer-pillars">

          <FooterPillar
            icon={<FaBuilding />}
            title={
              <>
                DIVERSE
                <br />
                PORTFOLIO
              </>
            }
          />

          <FooterPillar
            icon={<FaGlobe />}
            title={
              <>
                GLOBAL
                <br />
                PRESENCE
              </>
            }
          />

          <FooterPillar
            icon={<FaShieldAlt />}
            title={
              <>
                TRUSTED
                <br />
                EXCELLENCE
              </>
            }
          />

          <FooterPillar
            icon={<FaHandshake />}
            title={
              <>
                DELIVERING
                <br />
                VALUE
              </>
            }
          />

          <FooterPillar
            icon={<FaChartLine />}
            title={
              <>
                GROWING
                <br />
                TOGETHER
              </>
            }
          />

        </div>

      </footer>

    </div>
  );
}


/* ============================================================
   SECTION HEADING COMPONENT
   ============================================================ */

function SectionHeading({ title }) {

  return (

    <div className="kk-section-heading">

      <span />

      <h2>
        {title}
      </h2>

      <span />

    </div>

  );
}


/* ============================================================
   CAPABILITY COMPONENT
   ============================================================ */

function Capability({
  icon,
  title,
  text
}) {

  return (

    <div className="kk-capability">

      <div className="kk-capability-icon">
        {icon}
      </div>

      <h3>
        {title}
      </h3>

      <p>
        {text}
      </p>

    </div>

  );

}


/* ============================================================
   PRODUCT COMPONENT
   ============================================================ */

function ProductCard({
  image,
  title,
  text
}) {

  return (

    <div className="kk-product">

      <div className="kk-product-image">

        <img
          src={image}
          alt={title}
        />

      </div>

      <div className="kk-product-content">

        <h3>
          {title}
        </h3>

        <p>
          {text}
        </p>

      </div>

    </div>

  );

}


/* ============================================================
   VALUE COMPONENT
   ============================================================ */

function Value({
  icon,
  title,
  text
}) {

  return (

    <div className="kk-value">

      <div className="kk-value-icon">
        {icon}
      </div>

      <h4>
        {title}
      </h4>

      <p>
        {text}
      </p>

    </div>

  );

}


/* ============================================================
   STAT COMPONENT
   ============================================================ */

function Stat({
  icon,
  text
}) {

  return (

    <div className="kk-stat">

      <div className="kk-stat-icon">
        {icon}
      </div>

      <span>
        {text}
      </span>

    </div>

  );

}


/* ============================================================
   FOOTER PILLAR
   ============================================================ */

function FooterPillar({
  icon,
  title
}) {

  return (

    <div className="kk-footer-pillar">

      <div>
        {icon}
      </div>

      <span>
        {title}
      </span>

    </div>

  );

}


/* ============================================================
   CSS
   ============================================================ */

const css = `

/* ============================================================
   GLOBAL
   ============================================================ */

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
}

.kk-page {
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  background: #faf8f4;
  color: #222;
  font-family: Arial, Helvetica, sans-serif;
}


/* ============================================================
   TOP NAV
   ============================================================ */

.kk-top-nav {
  width: 100%;
  background: #111;
  padding: 14px 25px;
}

.kk-back-button {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 11px 18px;
  border: none;
  border-radius: 5px;
  background: #8b0000;
  color: white;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.25s;
}

.kk-back-button:hover {
  background: #a30000;
  transform: translateY(-1px);
}


/* ============================================================
   HERO
   ============================================================ */

.kk-hero {
  width: 100%;
  display: flex;
  min-height: 650px;
  background: #faf8f4;
}

.kk-hero-left {
  width: 48%;
  padding: 45px 5%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.kk-hero-right {
  width: 52%;
  min-height: 650px;
}

.kk-logo-container {
  width: 100%;
  text-align: center;
  margin-bottom: 15px;
}

.kk-logo {
  width: 100%;
  max-width: 560px;
  max-height: 240px;
  object-fit: contain;
  display: block;
  margin: auto;
}

.kk-tagline {
  margin: 15px 0;
  text-align: center;
  font-family: Georgia, "Times New Roman", serif;
  font-size: clamp(21px, 2.3vw, 32px);
  color: #222;
}

.kk-description {
  max-width: 750px;
  margin: 0 auto 28px;
  font-size: 16px;
  line-height: 1.65;
  color: #444;
}

.kk-description strong {
  color: #8b0000;
}


/* ============================================================
   HERO PILLARS
   ============================================================ */

.kk-hero-pillars {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-top: 1px solid #d5c7bb;
  padding-top: 22px;
}

.kk-hero-pillar {
  padding: 5px 13px;
  text-align: center;
  border-right: 1px solid #d5c7bb;
}

.kk-hero-pillar:last-child {
  border-right: none;
}

.kk-hero-pillar svg {
  font-size: 28px;
  color: #8b0000;
  margin-bottom: 9px;
}

.kk-hero-pillar h4 {
  margin: 0 0 7px;
  font-size: 12px;
  line-height: 1.25;
}

.kk-hero-pillar p {
  margin: 0;
  font-size: 11px;
  color: #666;
  line-height: 1.45;
}


/* ============================================================
   HERO IMAGE
   ============================================================ */

.kk-hero-image-container {
  width: 100%;
  height: 100%;
  min-height: 650px;
  position: relative;
  overflow: hidden;
}

.kk-hero-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.kk-hero-overlay {
  position: absolute;
  left: 7%;
  right: 7%;
  bottom: 7%;
  padding: 30px;
  background: rgba(110, 0, 0, 0.92);
  color: white;
  border-radius: 12px;
}

.kk-hero-overlay h2 {
  margin: 0;
  font-family: Georgia, "Times New Roman", serif;
  font-size: clamp(25px, 3vw, 43px);
  line-height: 1.15;
}

.kk-hero-divider {
  text-align: center;
  color: #f0df9e;
  margin: 13px 0;
}

.kk-hero-overlay p {
  margin: 0;
  font-size: 15px;
  line-height: 1.55;
}


/* ============================================================
   SECTION HEADING
   ============================================================ */

.kk-section-heading {
  width: 100%;
  padding: 34px 5% 22px;
  background: #faf8f4;
  display: flex;
  align-items: center;
  gap: 20px;
}

.kk-section-heading span {
  flex: 1;
  height: 2px;
  background: #8b0000;
}

.kk-section-heading h2 {
  margin: 0;
  color: #8b0000;
  font-size: clamp(18px, 2vw, 29px);
  letter-spacing: 2px;
  text-align: center;
  white-space: nowrap;
}


/* ============================================================
   CAPABILITIES
   ============================================================ */

.kk-capabilities {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  padding: 35px 4%;
  background: #8b0000;
}

.kk-capability {
  padding: 8px 18px;
  text-align: center;
  color: white;
  border-right: 1px solid rgba(255,255,255,0.35);
}

.kk-capability:last-child {
  border-right: none;
}

.kk-capability-icon svg {
  color: #f0df9e;
  font-size: 35px;
  margin-bottom: 13px;
}

.kk-capability h3 {
  margin: 0 0 11px;
  font-size: 13px;
  line-height: 1.3;
}

.kk-capability p {
  margin: 0;
  color: #f4dddd;
  font-size: 11px;
  line-height: 1.5;
}


/* ============================================================
   PRODUCTS
   ============================================================ */

.kk-products {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 15px;
  padding: 5px 4% 40px;
  background: #faf8f4;
}

.kk-product {
  background: white;
  border: 1px solid #d6c9bd;
  border-radius: 9px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  transition: 0.25s;
}

.kk-product:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 18px rgba(0,0,0,0.15);
}

.kk-product-image {
  width: 100%;
  height: 180px;
  overflow: hidden;
}

.kk-product-image img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  transition: 0.4s;
}

.kk-product:hover .kk-product-image img {
  transform: scale(1.05);
}

.kk-product-content {
  text-align: center;
  padding: 15px 10px;
  min-height: 95px;
}

.kk-product-content h3 {
  margin: 0 0 8px;
  color: #8b0000;
  font-size: 15px;
}

.kk-product-content p {
  margin: 0;
  color: #555;
  font-size: 12px;
  line-height: 1.5;
}


/* ============================================================
   FOUNDATION + VALUES
   ============================================================ */

.kk-foundation-values {
  width: 100%;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 20px;
  padding: 0 4% 35px;
  background: #faf8f4;
}

.kk-foundation-card {
  display: flex;
  min-height: 285px;
  background: #eee8e0;
  border: 1px solid #d2c3b7;
  border-radius: 12px;
  overflow: hidden;
}

.kk-foundation-text {
  width: 58%;
  padding: 35px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.kk-foundation-text h3 {
  margin: 0 0 16px;
  color: #8b0000;
  font-size: 18px;
  line-height: 1.3;
}

.kk-foundation-text p {
  margin: 0;
  color: #444;
  font-size: 14px;
  line-height: 1.65;
}

.kk-legacy-image {
  width: 42%;
  position: relative;
  overflow: hidden;
}

.kk-legacy-image img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.kk-legacy-image span {
  position: absolute;
  top: 18px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(139,0,0,0.9);
  color: white;
  padding: 6px 15px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 2px;
}


/* ============================================================
   VALUES
   ============================================================ */

.kk-values-card {
  padding: 30px;
  background: #8b0000;
  color: white;
  border-radius: 12px;
}

.kk-values-card > h3 {
  margin: 0 0 25px;
  text-align: center;
  font-size: 20px;
  letter-spacing: 2px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(255,255,255,0.4);
}

.kk-values {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
}

.kk-value {
  text-align: center;
}

.kk-value-icon svg {
  font-size: 32px;
  color: #f0df9e;
  margin-bottom: 9px;
}

.kk-value h4 {
  margin: 0 0 7px;
  font-size: 13px;
}

.kk-value p {
  margin: 0;
  color: #f4dddd;
  font-size: 11px;
  line-height: 1.45;
}


/* ============================================================
   MOTTO
   ============================================================ */

.kk-motto {
  width: 100%;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  align-items: center;
  padding: 30px 5%;
  background: #8b0000;
  color: white;
}

.kk-motto-text {
  display: flex;
  align-items: center;
  gap: 18px;
  padding-right: 30px;
  border-right: 1px solid rgba(255,255,255,0.4);
}

.kk-motto-text > svg {
  color: #f0df9e;
  font-size: 28px;
  flex-shrink: 0;
}

.kk-motto-text strong {
  display: block;
  font-size: clamp(14px, 1.4vw, 21px);
  line-height: 1.4;
}

.kk-motto-gold {
  color: #f0df9e;
  margin-top: 7px;
}

.kk-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding-left: 30px;
}

.kk-stat {
  text-align: center;
}

.kk-stat-icon svg {
  font-size: 27px;
  color: #f0df9e;
  margin-bottom: 8px;
}

.kk-stat span {
  display: block;
  font-size: 10px;
  line-height: 1.35;
  font-weight: 800;
}


/* ============================================================
   FOOTER
   ============================================================ */

.kk-footer {
  width: 100%;
  display: grid;
  grid-template-columns: 100px 1fr auto;
  align-items: center;
  gap: 30px;
  padding: 25px 5%;
  background: #faf8f4;
  border-top: 2px solid #8b0000;
}

.kk-footer-number {
  background: #8b0000;
  color: white;
  font-size: 55px;
  line-height: 1;
  font-weight: 900;
  text-align: center;
  padding: 12px 5px;
  transform: skew(-8deg);
}

.kk-footer-company {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.kk-footer-company strong {
  color: #8b0000;
  font-size: 20px;
}

.kk-footer-company span {
  color: #666;
  font-family: Georgia, serif;
  font-size: 14px;
  font-style: italic;
}

.kk-footer-pillars {
  display: flex;
  gap: 25px;
}

.kk-footer-pillar {
  min-width: 70px;
  text-align: center;
  color: #444;
  font-size: 9px;
  line-height: 1.3;
  font-weight: 800;
}

.kk-footer-pillar > div {
  color: #8b0000;
  font-size: 25px;
  margin-bottom: 6px;
}


/* ============================================================
   TABLET
   ============================================================ */

@media (max-width: 1200px) {

  .kk-capabilities {
    grid-template-columns: repeat(3, 1fr);
  }

  .kk-products {
    grid-template-columns: repeat(3, 1fr);
  }

}


/* ============================================================
   TABLET / SMALL LAPTOP
   ============================================================ */

@media (max-width: 900px) {

  .kk-hero {
    flex-direction: column;
  }

  .kk-hero-left {
    width: 100%;
    padding: 40px 6%;
  }

  .kk-hero-right {
    width: 100%;
    min-height: 550px;
  }

  .kk-hero-image-container {
    min-height: 550px;
  }

  .kk-foundation-values {
    grid-template-columns: 1fr;
  }

  .kk-motto {
    grid-template-columns: 1fr;
    gap: 25px;
  }

  .kk-motto-text {
    padding-right: 0;
    padding-bottom: 25px;
    border-right: none;
    border-bottom: 1px solid rgba(255,255,255,0.4);
  }

  .kk-stats {
    padding-left: 0;
  }

  .kk-footer {
    grid-template-columns: 80px 1fr;
  }

  .kk-footer-pillars {
    grid-column: 1 / -1;
    justify-content: space-around;
  }

}


/* ============================================================
   MOBILE
   ============================================================ */

@media (max-width: 650px) {

  .kk-top-nav {
    padding: 10px 12px;
  }

  .kk-hero-left {
    padding: 30px 20px;
  }

  .kk-hero-right {
    min-height: 420px;
  }

  .kk-hero-image-container {
    min-height: 420px;
  }

  .kk-hero-overlay {
    left: 4%;
    right: 4%;
    bottom: 4%;
    padding: 20px;
  }

  .kk-hero-overlay p {
    font-size: 12px;
  }

  .kk-hero-pillars {
    grid-template-columns: repeat(2, 1fr);
    row-gap: 20px;
  }

  .kk-hero-pillar:nth-child(2) {
    border-right: none;
  }

  .kk-capabilities {
    grid-template-columns: repeat(2, 1fr);
    padding: 20px 12px;
  }

  .kk-capability {
    border-right: none;
    border-bottom: 1px solid rgba(255,255,255,0.25);
    padding: 20px 10px;
  }

  .kk-products {
    grid-template-columns: repeat(2, 1fr);
    padding-left: 12px;
    padding-right: 12px;
  }

  .kk-product-image {
    height: 160px;
  }

  .kk-foundation-card {
    flex-direction: column;
  }

  .kk-foundation-text {
    width: 100%;
  }

  .kk-legacy-image {
    width: 100%;
    min-height: 250px;
  }

  .kk-values {
    grid-template-columns: repeat(2, 1fr);
  }

  .kk-stats {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .kk-footer {
    grid-template-columns: 70px 1fr;
    gap: 15px;
    padding: 20px;
  }

  .kk-footer-company strong {
    font-size: 15px;
  }

  .kk-footer-company span {
    font-size: 11px;
  }

  .kk-footer-pillars {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 15px;
  }

}


/* ============================================================
   VERY SMALL MOBILE
   ============================================================ */

@media (max-width: 430px) {

  .kk-back-button {
    font-size: 12px;
    padding: 10px 13px;
  }

  .kk-section-heading {
    gap: 8px;
    padding-left: 12px;
    padding-right: 12px;
  }

  .kk-section-heading h2 {
    font-size: 14px;
    letter-spacing: 1px;
  }

  .kk-capabilities {
    grid-template-columns: 1fr;
  }

  .kk-products {
    grid-template-columns: 1fr;
  }

  .kk-product-image {
    height: 210px;
  }

  .kk-values {
    grid-template-columns: 1fr 1fr;
  }

  .kk-motto-text {
    gap: 8px;
  }

  .kk-motto-text strong {
    font-size: 12px;
  }

  .kk-footer {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .kk-footer-number {
    width: 80px;
    margin: auto;
  }

  .kk-footer-company {
    align-items: center;
  }

  .kk-footer-pillars {
    grid-template-columns: repeat(2, 1fr);
  }

}

`;


/* ============================================================
   INSERT CSS
   ============================================================ */

if (
  typeof document !== 'undefined' &&
  !document.getElementById('kk-industries-css')
) {

  const style = document.createElement('style');

  style.id = 'kk-industries-css';

  style.innerHTML = css;

  document.head.appendChild(style);

}